<script setup>
/**
 * 「充值 / 提现」页，逐条对齐旧版 web/36.c43f840dbffc22920e7c.js 里的
 * app-money 路由页（类 r、模板工厂 B + 子模板 h/m/f/v/x/C/y/z/_/I/L、样式 k）。
 * 旧版路由表：{ path: '', component: r } 和 { path: 'type/:type', component: r }。
 *
 * 注意这跟牌九弹窗里的充值/提现（chunk 19 的 app-money）是**两个不同组件**，差异：
 *   1. 标题只有「充值」/「提现」两个字，弹窗版是「上分申请」/「下分申请」
 *   2. 标签是「充值方式：」「充值金额：」，弹窗版是「上分方式：」「上分金额：」
 *   3. 表单里 bank 字段是 [""]，**没有任何校验器**；弹窗版是 [required, min(1)]
 *      所以这一页只要填了金额就能点「确 定」，即使没选方式
 *   4. getBankList() 拿到列表后会**自动选中第一张**（bankList[0].bank_id）并 changBank()
 *   5. 提现时如果没有收款账号（且 error_code != 1001）会弹 presentAlertConfirm()：
 *      {header:'提示', message:'请设置收款账号后再试', buttons:[取消, 确定→/user/bank/add]}
 *   6. ion-select 多绑了 selectedText = selectedBank?.bank_title
 *   7. 提交成功用 showAlert('提示', '...申请提交成功') 然后 navController.back()，
 *      弹窗版用的是 toast
 *   8. 样式里 .bank-title 的 margin-bottom 是 10px（弹窗版是 3px）
 *
 * 生命周期：
 *   构造函数：读路由参数 type（parseInt），并 getUserMoney()
 *   ngOnInit：建表单
 *   ionViewDidEnter：getBankList()
 */
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { request } from '../services/api'
import LegacySelect from '../components/LegacySelect.vue'
import LegacyAlert from '../components/LegacyAlert.vue'
import { moneySuccessMessage } from '../services/money-success'

const route = useRoute()
const router = useRouter()

// 旧版 this.type = parseInt(params.type)，默认 1
const formType = ref(String(route.params.type || '1'))
const bank = ref('')
const money = ref('')
const bankList = ref([])
const selectedBank = ref(null)
// 旧版 this.user = {username, nickname, balance, frozen}
const user = ref({ username: '', nickname: '', balance: 0, frozen: 0 })
const submitting = ref(false)
const notice = ref('')
// 旧版 presentAlertConfirm 弹的那个「请设置收款账号后再试」
const showBankAlert = ref(false)

const isRecharge = computed(() => Number(formType.value) === 1)
// 旧版标题和标签都靠这两个字切换
const actionWord = computed(() => (isRecharge.value ? '充值' : '提现'))

// 旧版 DecimalPipe '.2-2'
const decimal2 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
function fmt2(value) { return decimal2.format(Number(value || 0)) }

// 旧版交易类型的两个 ion-select-option
const typeOptions = [
  { value: '1', label: '充值' },
  { value: '2', label: '提现' },
]
const bankOptions = computed(() => bankList.value.map((item) => ({ value: String(item.bank_id), label: item.bank_title })))
/** 旧版 [disabled]="0 == bankList.length" */
const bankDisabled = computed(() => bankList.value.length === 0)

/**
 * 旧版 [disabled]="!formGroup.valid"。
 * 这一页的表单只有 type 和 money 有校验器（required + min(1)），bank 是 [""] 没有校验，
 * 所以没选方式也能提交 —— 这点跟牌九弹窗版不一样，照旧版实现。
 */
const formValid = computed(() => Number(formType.value) >= 1 && Number(money.value) >= 1)

/** 旧版 _：*ngIf="2 == type && user.balance > 0" 的「可用金额」行 */
const showBalance = computed(() => !isRecharge.value && Number(user.value?.balance) > 0)

/** 旧版 getUserMoney()，在构造函数里调用 */
async function getUserMoney() {
  try {
    const response = await request('/user/money')
    user.value = response.data || user.value
  } catch (error) {
    notice.value = error.message || '加载失败'
  }
}

/** 旧版 getBankList() */
async function getBankList() {
  bank.value = ''
  selectedBank.value = null
  notice.value = ''
  const recharge = isRecharge.value
  try {
    const response = await request(recharge ? '/recharge/bank' : '/bank/lists')
    const data = response.data || []
    if (data.length > 0) {
      bankList.value = data
      // 旧版拿到列表后会自动选中第一项
      bank.value = String(data[0].bank_id)
      changBank()
    } else if (recharge) {
      // 旧版充值分支的文案是写死的
      notice.value = '系统错误，请联系管理员'
    } else {
      bankList.value = []
      // 旧版提现分支：error_code != 1001 时弹「请设置收款账号后再试」
      showBankAlert.value = true
    }
  } catch (error) {
    if (recharge) {
      notice.value = '系统错误，请联系管理员'
    } else {
      bankList.value = []
      notice.value = error.message || '加载失败'
      showBankAlert.value = true
    }
  }
}

/** 旧版 changBank() */
function changBank() {
  selectedBank.value = bankList.value.find((item) => String(item.bank_id) === String(bank.value)) || null
}

/** 旧版交易类型 ion-select 的 ionChange → getBankList() */
function onTypeChange() { void getBankList() }

/** 旧版 presentAlertConfirm 的「确定」→ navigateForward('/user/bank/add') */
function goAddBank() {
  showBankAlert.value = false
  router.push('/user/bank/add')
}

/** 旧版 saveData() */
async function saveData() {
  if (!formValid.value || submitting.value || moneySuccessMessage.value) return
  // 旧版提现分支在发请求前先比余额
  if (!isRecharge.value && parseFloat(money.value) > Number(user.value?.balance || 0)) {
    notice.value = '余额不足'
    return
  }
  submitting.value = true
  notice.value = '正在提交，请稍等'
  try {
    const path = isRecharge.value ? '/recharge/create' : '/cash/create'
    const response = await request(path, { method: 'POST', data: { money: money.value, bank: bank.value } })
    // 旧版成功条件是 error_code === 0 && data.length > 0
    if (!(response.data && response.data.length > 0)) {
      notice.value = '提交失败，请稍后再试'
      return
    }
    // 旧版这里用的是 showAlert（不是 toast），然后 navController.back()
    notice.value = ''
    moneySuccessMessage.value = `${actionWord.value}申请提交成功`
    if (window.history.state?.back) router.back()
    else await router.replace('/user/profile')
  } catch (error) {
    notice.value = error.message || '提交失败'
  } finally {
    submitting.value = false
  }
}

// 路由上的 type 变了要跟着切（旧版是 params.subscribe）
watch(() => route.params.type, (next) => {
  if (next) { formType.value = String(next); void getBankList() }
})

// 旧版：构造函数 getUserMoney()，ionViewDidEnter getBankList()
onMounted(() => { void getUserMoney(); void getBankList() })
</script>

<template>
  <div class="money-page">
    <!-- 旧版 ion-header > ion-toolbar：返回按钮 routerLink="/tabs/user"，
         标题只有「充值」或「提现」两个字 -->
    <header class="modal-header">
      <RouterLink class="modal-back" to="/user/profile" aria-label="返回">
        <svg class="modal-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" /></svg>
      </RouterLink>
      <h1 class="modal-title">{{ actionWord }}</h1>
      <span class="modal-spacer" aria-hidden="true"></span>
    </header>

    <!-- 旧版 ion-content[padding] -->
    <div class="modal-content">
      <form novalidate @submit.prevent="saveData">
        <section class="ion-list">
          <!-- 交易类型 -->
          <div class="list-item">
            <div class="list-item-inner">
              <div class="ion-label">交易类型：</div>
              <LegacySelect
                v-model="formType"
                :options="typeOptions"
                alert-header="交易类型："
                cancel-text="取消"
                ok-text="确定"
                @change="onTypeChange"
              />
            </div>
          </div>

          <!-- 充值方式 / 提现方式 -->
          <div class="list-item" :class="{ 'item-interactive-disabled': bankDisabled }">
            <div class="list-item-inner">
              <div class="ion-label">{{ actionWord }}方式：</div>
              <LegacySelect
                v-model="bank"
                :options="bankOptions"
                :disabled="bankDisabled"
                :alert-header="`${actionWord}方式：`"
                cancel-text="取消"
                ok-text="确定"
                placeholder="请选择交易方式"
                @change="changBank"
              />
            </div>
          </div>

          <!-- 旧版 y：*ngIf="bankValue.value > 0 && selectedBank" -->
          <div v-if="Number(bank) > 0 && selectedBank" class="list-item">
            <div class="list-item-inner">
              <div class="bank-item">
                <span class="bank-title">{{ selectedBank.bank_title }}</span>
                <span class="bank-username">{{ selectedBank.bank_username }}</span>
                <span class="bank-account">{{ selectedBank.bank_account }}</span>
              </div>
            </div>
          </div>

          <!-- 旧版 z：充值且该收款方式带收款码时显示 -->
          <div v-if="Number(bank) > 0 && selectedBank && isRecharge && selectedBank.bank_picture_url" class="list-item">
            <div class="list-item-inner">
              <div class="bank-item-img">
                <img class="bank-image" :src="selectedBank.bank_picture_url" alt="" />
              </div>
            </div>
          </div>

          <!-- 旧版 _：*ngIf="2 == type && user.balance > 0" -->
          <div v-if="showBalance" class="list-item">
            <div class="list-item-inner">
              <div class="ion-label">可用金额：</div>
              <div class="money"> {{ fmt2(user.balance) }} </div>
            </div>
          </div>

          <!-- 充值金额 / 提现金额 -->
          <div class="list-item">
            <div class="list-item-inner">
              <div class="ion-label">{{ actionWord }}金额：</div>
              <div class="ion-input">
                <input v-model="money" class="native-input" type="text" placeholder="请输入交易金额" />
                <button v-if="money" class="input-clear-icon" type="button" aria-label="清除" @click="money = ''"></button>
              </div>
            </div>
          </div>
        </section>

        <!-- 旧版 div.login-action > ion-button color="secondary" expand="full"，文本「确 定」中间带空格 -->
        <div class="login-action">
          <button
            class="ion-button button-full button-solid ion-color-secondary"
            type="button"
            :class="{ 'button-disabled': !formValid || submitting }"
            :disabled="!formValid || submitting"
            @click="saveData"
          >
            <span class="button-inner">确 定</span>
          </button>
        </div>

        <!-- 旧版 ion-text[color=warning] > p.warning-tip -->
        <div class="ion-text-warning">
          <p class="warning-tip"> 提示：操作完成后请及时联系管理员或代理。 </p>
        </div>

        <p v-if="notice" class="modal-notice">{{ notice }}</p>
      </form>
    </div>

    <!-- 旧版 presentAlertConfirm()：提现时没有收款账号就弹这个 -->
    <Teleport to="body">
      <LegacyAlert
        v-if="showBankAlert"
        header="提示"
        message="请设置收款账号后再试"
        cancel-text="取消"
        ok-text="确定"
        @cancel="showBankAlert = false"
        @confirm="goAddBank"
      />
    </Teleport>
  </div>
</template>

<style scoped>
/* 页面外壳与顶栏跟其它旧版还原页保持一致 */
.money-page{position:fixed;z-index:1;top:0;left:0;width:100%;height:100%;background:#fff;color:#000;display:flex;flex-direction:column;font:14px Arial,"Microsoft YaHei",sans-serif}
.modal-header{display:flex;align-items:center;height:56px;background:var(--legacy-theme,#f95454);color:var(--legacy-toolbar-color,#fff);position:relative;z-index:1;box-shadow:0 2px 4px rgb(0 0 0 / 20%)}
.modal-back{display:flex;align-items:center;justify-content:center;width:56px;height:56px;border:0;background:transparent;line-height:1;color:inherit;cursor:pointer;padding:0;text-decoration:none}
.modal-back .modal-icon{width:24px;height:24px;display:block}
.modal-title{flex:1;margin:0;font-size:18px;font-weight:500;text-align:center;color:inherit}
.modal-spacer{width:56px;flex:none}
/* ion-content[padding] → 四边 16px */
.modal-content{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:16px}

/* --- Ionic 4 md 的真实默认值，取自 web/135.773ae5d979ae5c36fbef.js --- */
.ion-list{display:block;margin:0;padding:8px 0;background:#fff;list-style-type:none}
.list-item{display:flex;align-items:center;justify-content:space-between;width:100%;min-height:48px;margin:0;padding:0 0 0 16px;background:#fff;color:#000;font-size:.8rem;overflow:hidden;position:relative;box-sizing:border-box}
/* space-between 对应 .item-native；ion-input 自带 flex:1，所以会紧跟在 label 后面 */
.list-item-inner{display:flex;flex:1;flex-direction:inherit;align-items:inherit;justify-content:space-between;align-self:stretch;min-height:inherit;padding:0 16px 0 0;border-width:0 0 1px 0;border-style:solid;border-color:rgba(0,0,0,.13);overflow:hidden;box-sizing:border-box}
/* ion-label（md）本身不带 flex，宽度按内容走 */
.ion-label{display:block;font-size:inherit;text-overflow:ellipsis;white-space:nowrap;overflow:hidden}
.item-interactive-disabled .ion-label{cursor:default;opacity:.3;pointer-events:none}
.money{text-align:right}

/* ion-input（md）。padding 取的是 Ionic 4 默认值，未从旧版打包产物逐条核对。 */
.ion-input{display:flex;align-items:center;flex:1;position:relative;padding:10px 0 11px 8px;font-size:inherit}
.ion-input .native-input{flex:1;width:100%;margin:0;padding:0;border:0;outline:none;background:transparent;color:inherit;font:inherit;box-sizing:border-box}
.ion-input .native-input::placeholder{color:currentColor;opacity:.33}
.input-clear-icon{width:30px;height:30px;border:0;background:transparent;cursor:pointer;padding:0;position:relative;opacity:.5}
.input-clear-icon::before,.input-clear-icon::after{content:"";position:absolute;left:9px;top:14px;width:12px;height:1.5px;background:currentColor}
.input-clear-icon::before{transform:rotate(45deg)}
.input-clear-icon::after{transform:rotate(-45deg)}

/* ion-button color="secondary" expand="full"，取自 web/75（md） */
.login-action{margin-top:8px}
.ion-button{
  display:flex;align-items:center;justify-content:center;
  width:100%;margin:4px 0;border:0;border-radius:0;
  height:36px;font-size:14px;font-weight:500;letter-spacing:.06em;text-transform:uppercase;cursor:pointer;
  background:var(--ion-color-secondary,#0cd1e8);color:#fff;
  box-shadow:0 3px 1px -2px rgb(0 0 0 / 20%),0 2px 2px 0 rgb(0 0 0 / 14%),0 1px 5px 0 rgb(0 0 0 / 12%);
}
.ion-button.button-disabled{cursor:default;opacity:.5;pointer-events:none;box-shadow:none}
.ion-button .button-inner{display:flex;flex-flow:row nowrap;flex-shrink:0;align-items:center;justify-content:center;width:100%;height:100%}

/* ion-text color="warning" → --ion-color-warning #ffce00 */
.ion-text-warning{color:var(--ion-color-warning,#ffce00)}

/* --- 以下逐条照抄旧版样式表 k（chunk 36）。
       注意 .bank-title 的 margin-bottom 是 10px，牌九弹窗版（chunk 19 的 zn）是 3px --- */
.bank-item{height:80px;border-radius:10px;margin-bottom:5px;margin-top:5px;padding:10px;position:relative;background:#62b900;color:#fff;width:100%}
.bank-item .bank-title{font-size:.8rem;display:block;margin-bottom:10px}
.bank-item .bank-username{font-size:.8rem;display:block;position:absolute;right:30px;top:10px}
.bank-item .bank-account{font-size:1.5rem;display:inline-block}
.warning-tip{font-size:.8rem}

/* 旧版没给 .bank-item-img / .bank-image 写样式，这里只保证收款码不溢出 */
.bank-item-img{width:100%;text-align:center}
.bank-image{max-width:100%;display:inline-block}

.modal-notice{margin:10px 0 0;color:#999;font-size:.8rem;text-align:center}
</style>
